"use client";

import { useRef, useState, useCallback } from "react";
import {
  Download,
  Package,
  Loader2,
  CheckCircle2,
  CloudUpload,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { exportWorkspaceAsZip, importFile, type ImportResult } from "@/services/exportImportService";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import ImportModal from "@/components/tailwind/import/ImportModal";
import { FORMAT_CARDS } from "@/components/tailwind/import/formatCards";
import SyncedDatabasesSection from "@/components/tailwind/settings/tabs/SyncedDatabasesSection";

// ── Types ─────────────────────────────────────────────────────────────────────

type ImportTab = "discover" | "completed";

interface CompletedImport extends ImportResult {
  id: string;
  fileType: string;
  importedAt: Date;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ImportExportContent() {
  const { currentWorkspace } = useWorkspaceContext();
  const workspaceId = currentWorkspace?._id || "";

  const [exportLoading, setExportLoading] = useState(false);
  const [importTab, setImportTab] = useState<ImportTab>("discover");
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [completed, setCompleted] = useState<CompletedImport[]>([]);
  const [csvModalFile, setCsvModalFile] = useState<File | null>(null);

  const dropInputRef = useRef<HTMLInputElement>(null);
  const cardInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // ── Export workspace ──────────────────────────────────────────────────────

  const handleExportWorkspace = async () => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    setExportLoading(true);
    await exportWorkspaceAsZip(workspaceId);
    setExportLoading(false);
  };

  // ── Import helpers ────────────────────────────────────────────────────────

  const runImport = useCallback(async (file: File) => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    setIsImporting(true);
    const res = await importFile(file, workspaceId, "workspace", workspaceId);
    setIsImporting(false);
    if (res) {
      setCompleted((prev) => [{
        ...res,
        id: crypto.randomUUID(),
        fileType: ext.toUpperCase(),
        importedAt: new Date(),
      }, ...prev]);
      setImportTab("completed");
      const detail =
        res.blockCount != null ? `${res.blockCount} blocks` :
        res.rowCount != null ? `${res.rowCount} rows` :
        res.pageCount != null ? `${res.pageCount} pages` : "";
      toast.success(`Imported "${res.title}"${detail ? ` · ${detail}` : ""}`);
    }
  }, [workspaceId]);

  const pickFile = useCallback((file: File) => {
    if (!workspaceId) { toast.error("No workspace selected"); return; }
    const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
    const valid = [".zip", ".csv", ".md", ".txt", ".html", ".htm", ".pdf", ".docx", ".enex"];
    if (!valid.includes(ext)) {
      toast.error(`Unsupported type: ${ext}`);
      return;
    }
    if (ext === ".csv") {
      setCsvModalFile(file);
      return;
    }
    runImport(file);
  }, [runImport, workspaceId]);

  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) pickFile(f);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-10 max-w-2xl">

      {/* ── EXPORT ── */}
      <section>
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">Export</h3>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
          Download all workspace content as Markdown, CSV, and ZIP.
        </p>
        <div className="border border-component rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center flex-shrink-0">
                <Package className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Export entire workspace</p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">All pages as .md · All databases as .csv · Bundled as .zip</p>
              </div>
            </div>
            <button
              type="button"
              title="Export the entire workspace"
              onClick={handleExportWorkspace}
              disabled={exportLoading || !workspaceId}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg hover-component  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {exportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exportLoading ? "Exporting…" : "Export"}
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
          You can also export individual pages by right-clicking any page in the sidebar.
        </p>
      </section>

      {/* ── IMPORT ── */}
      <section>
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 bg-zinc-100 dark:bg-zinc-800 rounded-lg p-1 w-fit">
          {(["discover", "completed"] as ImportTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              title={tab === "completed" ? "View completed imports" : "Discover import options"}
              onClick={() => setImportTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors capitalize ${
                importTab === tab
                  ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 shadow-sm"
                  : "text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
              }`}
            >
              {tab === "completed" && completed.length > 0
                ? `Completed (${completed.length})`
                : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* ── Discover tab ── */}
        {importTab === "discover" && (
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">Import your content</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                If you import a ZIP file, we&apos;ll convert each file inside into its own page.
              </p>
            </div>

            {/* Hidden input for drop zone */}
            <input
              ref={dropInputRef}
              type="file"
              accept=".zip,.csv,.md,.txt,.html,.htm,.pdf,.docx,.enex"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickFile(f);
                e.target.value = "";
              }}
            />

            {/* Drop zone */}
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`rounded-xl border-2 border-dashed transition-colors px-8 py-10 text-center ${
                isDragging
                  ? "border-blue-400 bg-blue-50 dark:bg-blue-950/20"
                  : isImporting
                  ? "border-component bg-zinc-50 dark:bg-zinc-800/40"
                  : "border-component hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50/30 dark:hover:bg-blue-950/10"
              }`}
            >
              {isImporting ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
                  <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Importing…</p>
                  <p className="text-xs text-zinc-400">This may take a moment for large files</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <CloudUpload className="h-9 w-9 text-blue-400 mb-1" strokeWidth={1.5} />
                  <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                    Import your content
                  </p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Drag and drop ZIP, CSV, PDF, text, markdown, or HTML files, or{" "}
                    <button
                      type="button"
                      title="Choose a file to import"
                      className="text-blue-500 hover:underline focus:outline-none"
                      onClick={() => dropInputRef.current?.click()}
                    >
                      choose a file
                    </button>
                  </p>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    ZIP files can be a maximum of 50 MB
                  </p>
                </div>
              )}
            </div>

            {/* File-based imports */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">File-based imports</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                Import CSV, PDF, text, markdown, or HTML files to convert them to pages.
              </p>

              <div className="grid grid-cols-2 gap-3">
                {FORMAT_CARDS.map((card) => {
                  const Icon = card.icon;
                  return (
                    <button
                      key={card.id}
                      type="button"
                      disabled={isImporting}
                      title={`Import ${card.label}`}
                      onClick={() => cardInputRefs.current[card.id]?.click()}
                      className="flex items-start gap-3 p-4 rounded-xl border border-component hover:border-zinc-300 dark:hover:border-zinc-600 hover-component  transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${card.iconBg}`}>
                        <Icon className={`h-4 w-4 ${card.iconColor}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{card.label}</p>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">{card.description}</p>
                      </div>
                      {/* Hidden input per card */}
                      <input
                        ref={(el) => { cardInputRefs.current[card.id] = el; }}
                        type="file"
                        accept={card.accept}
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) pickFile(f);
                          e.target.value = "";
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Completed tab ── */}
        {importTab === "completed" && (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">Completed imports</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Your import history for this session.</p>
            </div>

            {completed.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12 rounded-xl border border-dashed border-component text-center">
                <CheckCircle2 className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                <p className="text-sm text-zinc-500 dark:text-zinc-400">No imports yet this session</p>
                <button
                  type="button"
                  title="Go to the Discover tab"
                  className="text-sm text-blue-500 hover:underline"
                  onClick={() => setImportTab("discover")}
                >
                  Go to Discover
                </button>
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-zinc-100 dark:divide-zinc-800 border border-component rounded-xl overflow-hidden">
                {completed.map((item) => {
                  const detail =
                    item.blockCount != null ? `${item.blockCount} blocks` :
                    item.rowCount != null ? `${item.rowCount} rows` :
                    item.pageCount != null ? `${item.pageCount} pages` : "";
                  return (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-zinc-400">
                          {item.fileType}{detail ? ` · ${detail}` : ""} · {item.importedAt.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>

      {/*
        Management for databases created via Import → "Keep in sync".
        Lives here rather than on the Connections tab because it is about
        content in the workspace, not about the OAuth grant.
      */}
      <section className="mt-8">
        <SyncedDatabasesSection />
      </section>

      {csvModalFile && (
        <ImportModal
          initialFile={csvModalFile}
          parentId={workspaceId}
          parentTable="workspace"
          onClose={() => setCsvModalFile(null)}
          onImportSuccess={(res) => {
            const ext = csvModalFile.name.split(".").pop()?.toUpperCase() || "CSV";
            setCompleted((prev) => [{
              ...res,
              id: crypto.randomUUID(),
              fileType: ext,
              importedAt: new Date(),
            }, ...prev]);
            setImportTab("completed");
            setCsvModalFile(null);
          }}
        />
      )}
    </div>
  );
}
